import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Mail, Play, RefreshCw, Network, Radar, Globe, Fingerprint, ShieldOff, Server, ScrollText, Bug } from "lucide-react";
import { toast } from "sonner";
import { http, apiError } from "@/lib/api";
import { useOrg } from "@/context/OrgContext";
import { useAuth } from "@/context/AuthContext";
import { Card, SectionTitle, Spinner, SeverityChip } from "@/components/Kit";
import { PostureDial } from "@/components/WebKit";
import { SpoofChip, RecordStatus, TakeoverBadge, ScoreTrend } from "@/components/AttackKit";

export default function DomainDetail() {
  const { id } = useParams();
  const { orgId } = useOrg();
  const { can } = useAuth();
  const canEmail = can("attack.email", "manage");
  const canAssets = can("attack.assets", "manage");
  const navigate = useNavigate();
  const [tab, setTab] = useState("email");
  const [domain, setDomain] = useState(null);
  const [posture, setPosture] = useState(null);
  const [history, setHistory] = useState([]);
  const [scanning, setScanning] = useState(false);

  const loadEmail = useCallback(async () => {
    if (!orgId) return;
    const [d, p, h] = await Promise.all([
      http.get(`/attack/domains/${id}`, { params: { org_id: orgId } }),
      http.get(`/attack/email/${id}`, { params: { org_id: orgId } }),
      http.get(`/attack/email/${id}/history`, { params: { org_id: orgId } }),
    ]);
    setDomain(d.data);
    setPosture(p.data && !p.data.empty ? p.data : null);
    setHistory((h.data || []).slice().reverse());
  }, [id, orgId]);

  useEffect(() => { loadEmail(); }, [loadEmail]);

  const runEmailScan = async () => {
    setScanning(true);
    try {
      const { data } = await http.post("/attack/email/scan", { org_id: orgId, domain_id: id });
      const started = Date.now();
      const poll = async () => {
        const r = await http.get(`/attack/email/scans/${data.id}`, { params: { org_id: orgId } });
        if (r.data.status === "completed") { await loadEmail(); toast.success("Email posture assessed"); setScanning(false); }
        else if (r.data.status === "failed") { toast.error(r.data.error_message || "Scan failed"); setScanning(false); }
        else if (Date.now() - started > 90000) { toast.error("Scan timed out"); setScanning(false); }
        else setTimeout(poll, 2000);
      };
      setTimeout(poll, 1500);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); setScanning(false); }
  };

  if (!domain) return <div className="p-6 text-[#8A8175]">Loading…</div>;

  return (
    <div className="p-6 max-w-[1400px] mx-auto fade-up" data-testid="domain-detail">
      <button onClick={() => navigate("/attack/domains")} className="flex items-center gap-1.5 text-sm text-[#8A8175] hover:text-[#A5300F] mb-4">
        <ArrowLeft className="w-4 h-4" /> Domains
      </button>

      <div className="flex items-center gap-3 mb-5">
        <Network className="w-7 h-7 text-[#A5300F]" />
        <div>
          <h1 className="font-display text-3xl font-bold text-[#323232]">{domain.registrable_domain}</h1>
          <p className="text-sm text-[#8A8175]">{domain.label}</p>
        </div>
      </div>

      <div className="flex gap-1 border-b border-[#ECE6DD] mb-5">
        {[["email", "Email Security", Mail], ["assets", "Assets & Subdomains", Radar], ["lookalike", "Lookalike Domains", Fingerprint], ["ct", "Certificates", ScrollText], ["exposure", "Exposed Surface", Bug]].map(([k, label, Icon]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${tab === k ? "border-[#A5300F] text-[#A5300F]" : "border-transparent text-[#8A8175] hover:text-[#A5300F]"}`}>
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      {tab === "email" && (
        <div className="space-y-6">
          <div className="flex justify-end">
            {canEmail && (
              <button onClick={runEmailScan} disabled={scanning} data-testid="run-email-scan-btn"
                className="flex items-center gap-2 bg-[#A5300F] text-white font-semibold text-sm px-4 py-2 rounded-md hover:bg-[#8F2A0D] disabled:opacity-60">
                {scanning ? <Spinner /> : posture ? <RefreshCw className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                {scanning ? "Scanning…" : posture ? "Re-scan email" : "Run email scan"}
              </button>
            )}
          </div>
          {!posture && !scanning && (
            <Card className="p-10 text-center text-[#B5AC9E]" data-testid="email-empty">
              No email posture yet. {canEmail ? "Run an email scan to assess SPF, DMARC, DKIM and transport security." : "Awaiting an analyst scan."}
            </Card>
          )}
          {posture && <EmailPosture snap={posture} history={history} />}
        </div>
      )}

      {tab === "assets" && (
        <AssetsTab domainId={id} orgId={orgId} canScan={canAssets} domain={domain} onScanned={loadEmail} />
      )}

      {tab === "lookalike" && (
        <LookalikesTab domainId={id} orgId={orgId} canScan={can("attack.domains", "manage")} />
      )}

      {tab === "ct" && (
        <CtTab domainId={id} orgId={orgId} canScan={canAssets} />
      )}

      {tab === "exposure" && (
        <ExposureTab domainId={id} orgId={orgId} canScan={canAssets} />
      )}
    </div>
  );
}

function EmailPosture({ snap, history }) {
  const spf = snap.spf || {}, dmarc = snap.dmarc || {}, dkim = snap.dkim || {};
  const mta = snap.mta_sts || {}, bimi = snap.bimi || {}, mx = snap.mx || [];
  const findings = snap.findings || [];
  const spfVal = !spf.present ? "Not published" : `Published (${spf.all_qualifier}all, ${spf.dns_lookups} lookups)`;
  const dmarcVal = !dmarc.present ? "Not published" : `p=${dmarc.policy || "?"} · pct=${dmarc.pct}`;
  const dkimVal = dkim.found?.length ? `${dkim.found.length} selector(s): ${dkim.found.map((s) => s.selector).join(", ")}` : "None discovered";
  const mtaVal = mta.dns?.present && mta.policy?.present ? `Enforced (${mta.policy.mode || "?"})` : "Not published";

  return (
    <div className="space-y-6" data-testid="email-posture">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="p-6 flex flex-col items-center justify-center">
          <div className="text-[11px] uppercase tracking-wider text-[#8A8175] font-semibold mb-3">Email Posture</div>
          <PostureDial score={snap.score} grade={snap.grade} />
        </Card>
        <Card className="p-6 lg:col-span-2">
          <div className="text-[11px] uppercase tracking-wider text-[#8A8175] font-semibold mb-2">Spoofability</div>
          <SpoofChip verdict={snap.spoofability?.verdict} />
          <p className="text-sm text-[#5C554C] mt-3">{snap.spoofability?.rationale}</p>
          <div className="text-[11px] text-[#B5AC9E] mt-4">
            Assessed {snap.scanned_at ? new Date(snap.scanned_at).toLocaleString("en-GB") : "—"} · {mx.length} MX record(s)
          </div>
        </Card>
      </div>

      <Card className="p-5">
        <SectionTitle className="mb-3">Posture Trend</SectionTitle>
        <ScoreTrend points={history} />
      </Card>

      <Card className="p-5">
        <SectionTitle className="mb-4">Authentication &amp; Transport</SectionTitle>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <RecordStatus label="SPF" value={spfVal} ok={spf.present && spf.all_qualifier === "-"} warn={spf.present && spf.all_qualifier !== "-"} />
          <RecordStatus label="DMARC" value={dmarcVal} ok={dmarc.present && dmarc.policy === "reject"} warn={dmarc.present && dmarc.policy !== "reject"} />
          <RecordStatus label="DKIM" value={dkimVal} ok={!!dkim.found?.length} warn={false} />
          <RecordStatus label="MTA-STS" value={mtaVal} ok={mta.dns?.present && mta.policy?.mode === "enforce"} warn={mta.dns?.present} />
          <RecordStatus label="TLS-RPT" value={snap.tls_rpt?.present ? "Published" : "Not published"} ok={snap.tls_rpt?.present} warn={!snap.tls_rpt?.present} />
          <RecordStatus label="DANE" value={snap.dane?.present ? "TLSA published" : (mx.length ? "Not published" : "N/A")} ok={snap.dane?.present} warn={!snap.dane?.present && mx.length > 0} />
          <RecordStatus label="BIMI" value={bimi.present ? (bimi.has_vmc ? "Published (VMC)" : "Published") : "Not published"} ok={bimi.present} warn={false} />
          <RecordStatus label="MX" value={mx.length ? mx.map((m) => m.host).slice(0, 3).join(", ") : "No MX"} ok={mx.length > 0} warn={mx.length === 0} />
          <RecordStatus label="DMARC RUA" value={dmarc.rua?.length ? dmarc.rua.join(", ") : "No aggregate reporting"} ok={!!dmarc.rua?.length} warn={!dmarc.rua?.length} />
        </div>
      </Card>

      <Card>
        <div className="px-5 py-3 border-b border-[#ECE6DD]"><SectionTitle>Findings ({findings.length})</SectionTitle></div>
        <div className="divide-y divide-[#F4EFE8]" data-testid="email-findings">
          {findings.map((fnd) => (
            <div key={fnd.rule_id} className="p-4 flex gap-4" data-testid={`finding-${fnd.rule_id}`}>
              <div className="shrink-0"><SeverityChip severity={fnd.severity} /></div>
              <div className="min-w-0">
                <div className="font-semibold text-sm text-[#323232]">{fnd.title}
                  <span className="ml-2 text-[10px] uppercase tracking-wider text-[#B5AC9E] font-bold">{fnd.category}</span></div>
                <div className="text-sm text-[#5C554C] mt-0.5">{fnd.detail}</div>
                <div className="text-xs text-[#8A8175] mt-1"><span className="font-semibold">Fix:</span> {fnd.recommendation}</div>
              </div>
            </div>
          ))}
          {findings.length === 0 && <div className="p-8 text-center text-[#2E7D32] font-semibold">No email-security issues detected.</div>}
        </div>
      </Card>
    </div>
  );
}

function AssetsTab({ domainId, orgId, canScan, domain, onScanned }) {
  const [assets, setAssets] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [summary, setSummary] = useState(null);

  const load = useCallback(() => {
    http.get("/attack/assets", { params: { org_id: orgId, domain_id: domainId } }).then((r) => setAssets(r.data));
  }, [orgId, domainId]);
  useEffect(() => { load(); }, [load]);

  const runDiscovery = async () => {
    setScanning(true); setSummary(null);
    try {
      const { data } = await http.post("/attack/discovery/scan", { org_id: orgId, domain_id: domainId });
      const started = Date.now();
      const poll = async () => {
        const r = await http.get(`/attack/discovery/scans/${data.id}`, { params: { org_id: orgId } });
        if (r.data.status === "completed") { setSummary(r.data.summary); load(); onScanned && onScanned(); toast.success("Discovery complete"); setScanning(false); }
        else if (r.data.status === "failed") { toast.error(r.data.error_message || "Discovery failed"); setScanning(false); }
        else if (Date.now() - started > 180000) { toast.message("Still running — check back shortly."); setScanning(false); }
        else setTimeout(poll, 3000);
      };
      setTimeout(poll, 2500);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); setScanning(false); }
  };

  const takeovers = assets.filter((a) => a.takeover?.vulnerable);

  return (
    <div className="space-y-5" data-testid="assets-tab">
      <div className="flex items-center justify-between">
        <p className="text-sm text-[#8A8175]">
          {assets.length} discovered asset{assets.length === 1 ? "" : "s"} · {assets.filter((a) => a.live).length} live · {takeovers.length} takeover risk
        </p>
        {canScan && (
          <button onClick={runDiscovery} disabled={scanning} data-testid="run-discovery-btn"
            className="flex items-center gap-2 bg-[#A5300F] text-white font-semibold text-sm px-4 py-2 rounded-md hover:bg-[#8F2A0D] disabled:opacity-60">
            {scanning ? <Spinner /> : <Radar className="w-4 h-4" />} {scanning ? "Discovering…" : "Run discovery"}
          </button>
        )}
      </div>

      {scanning && (
        <Card className="p-4 text-sm text-[#8A8175]">
          Querying crt.sh, CertSpotter, AlienVault OTX, Wayback &amp; HackerTarget, then resolving and probing hosts…
        </Card>
      )}
      {summary && (
        <Card className="p-4 text-xs text-[#5C554C]">
          <span className="font-semibold">Sources:</span> {Object.entries(summary.sources || {}).map(([k, v]) => `${k} ${v}`).join(" · ")} · probed {summary.probed} · wildcard DNS: {summary.wildcard_dns ? "yes" : "no"}
        </Card>
      )}

      {takeovers.length > 0 && (
        <Card className="border-[#A5300F]">
          <div className="px-5 py-3 border-b border-[#F0D9D0] bg-[#FCF1EE]"><SectionTitle className="text-[#A5300F]">Subdomain Takeover Risks ({takeovers.length})</SectionTitle></div>
          <div className="divide-y divide-[#F4EFE8]">
            {takeovers.map((a) => (
              <div key={a.hostname} className="p-4" data-testid={`takeover-${a.hostname}`}>
                <div className="flex items-center gap-2"><TakeoverBadge takeover={a.takeover} /><span className="font-semibold text-sm">{a.hostname}</span></div>
                <div className="text-xs text-[#5C554C] mt-1">CNAME → <span className="font-mono">{a.cname || "—"}</span></div>
                <div className="text-xs text-[#8A8175] mt-0.5">{a.takeover.evidence}</div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card>
        <table className="w-full text-sm" data-testid="assets-table">
          <thead><tr className="text-[11px] uppercase tracking-wider text-[#8A8175] text-left bg-[#FFF5EB] border-b border-[#ECE6DD]">
            <th className="py-2.5 px-4">Hostname</th><th>IP / CNAME</th><th className="text-center">Live</th><th>Ports / CVEs</th><th>Owner</th><th>Sources</th><th>Takeover</th>
          </tr></thead>
          <tbody>
            {assets.map((a) => {
              const en = a.enrichment || {};
              return (
              <tr key={a.hostname} className="border-b border-[#F4EFE8] hover:bg-[#FBF7F1]" data-testid={`asset-row-${a.hostname}`}>
                <td className="py-2.5 px-4 font-semibold flex items-center gap-2"><Globe className="w-3.5 h-3.5 text-[#B5AC9E]" />{a.hostname}</td>
                <td className="text-xs text-[#5C554C] font-mono">{a.cname ? `→ ${a.cname}` : (a.ip_addresses?.slice(0, 2).join(", ") || "unresolved")}</td>
                <td className="text-center">{a.live ? <span className="text-[#2E7D32] font-semibold text-xs">{a.http_status}</span> : <span className="text-[#B5AC9E] text-xs">—</span>}</td>
                <td className="text-xs">
                  {(en.ports || []).length > 0 && <span className="text-[#5C554C]"><Server className="w-3 h-3 inline mr-1" />{en.ports.slice(0, 6).join(", ")}</span>}
                  {(en.vulns || []).length > 0 && <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-white font-bold" style={{ background: "#A5300F" }} data-testid={`cve-${a.hostname}`}><ShieldOff className="w-3 h-3" />{en.vulns.length} CVE</span>}
                  {!(en.ports || []).length && !(en.vulns || []).length && <span className="text-[#B5AC9E]">—</span>}
                </td>
                <td className="text-xs text-[#8A8175]">{en.rdap?.name || "—"}</td>
                <td className="text-xs text-[#8A8175]">{(a.sources || []).join(", ")}</td>
                <td><TakeoverBadge takeover={a.takeover} /></td>
              </tr>
            );})}
            {assets.length === 0 && <tr><td colSpan={7} className="py-10 text-center text-[#B5AC9E]">No assets discovered yet. {canScan ? "Run discovery to enumerate subdomains." : "Awaiting an analyst scan."}</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
}


const LK_RISK = { high: "#A5300F", medium: "#E39629", low: "#7F5F52" };
const LK_STATUS = ["new", "monitoring", "benign", "malicious"];

function LookalikesTab({ domainId, orgId, canScan }) {
  const [rows, setRows] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [summary, setSummary] = useState(null);

  const load = useCallback(() => {
    http.get("/attack/lookalike", { params: { org_id: orgId, domain_id: domainId } }).then((r) => setRows(r.data));
  }, [orgId, domainId]);
  useEffect(() => { load(); }, [load]);

  const runScan = async () => {
    setScanning(true); setSummary(null);
    try {
      const { data } = await http.post("/attack/lookalike/scan", { org_id: orgId, domain_id: domainId });
      const started = Date.now();
      const poll = async () => {
        const r = await http.get(`/attack/lookalike/scans/${data.id}`, { params: { org_id: orgId } });
        if (r.data.status === "completed") { setSummary(r.data.summary); load(); toast.success("Lookalike scan complete"); setScanning(false); }
        else if (r.data.status === "failed") { toast.error(r.data.error_message || "Scan failed"); setScanning(false); }
        else if (Date.now() - started > 180000) { toast.message("Still running — check back shortly."); setScanning(false); }
        else setTimeout(poll, 3000);
      };
      setTimeout(poll, 2500);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); setScanning(false); }
  };

  const setStatus = async (row, status) => {
    try { await http.patch(`/attack/lookalike/${row.id}`, { status }, { params: { org_id: orgId } }); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5" data-testid="lookalike-tab">
      <div className="flex items-center justify-between">
        <p className="text-sm text-[#8A8175]">
          {rows.length} registered lookalike{rows.length === 1 ? "" : "s"} · {rows.filter((r) => r.dns_mx?.length).length} can receive mail
        </p>
        {canScan && (
          <button onClick={runScan} disabled={scanning} data-testid="run-lookalike-btn"
            className="flex items-center gap-2 bg-[#A5300F] text-white font-semibold text-sm px-4 py-2 rounded-md hover:bg-[#8F2A0D] disabled:opacity-60">
            {scanning ? <Spinner /> : <Fingerprint className="w-4 h-4" />} {scanning ? "Scanning…" : "Run lookalike scan"}
          </button>
        )}
      </div>
      {scanning && <Card className="p-4 text-sm text-[#8A8175]">Generating typo-squat permutations (dnstwist) and checking which are registered…</Card>}
      {summary && <Card className="p-4 text-xs text-[#5C554C]"><span className="font-semibold">Registered:</span> {summary.registered} · with MX {summary.with_mx} · with A {summary.with_a}</Card>}

      <Card>
        <table className="w-full text-sm" data-testid="lookalike-table">
          <thead><tr className="text-[11px] uppercase tracking-wider text-[#8A8175] text-left bg-[#FFF5EB] border-b border-[#ECE6DD]">
            <th className="py-2.5 px-4">Lookalike domain</th><th>Risk</th><th>Technique</th><th className="text-center">A / MX</th><th>Triage</th>
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.domain} className="border-b border-[#F4EFE8] hover:bg-[#FBF7F1]" data-testid={`lookalike-row-${r.domain}`}>
                <td className="py-2.5 px-4 font-semibold font-mono">{r.domain}</td>
                <td><span className="px-2 py-0.5 text-[11px] font-bold rounded uppercase" style={{ color: LK_RISK[r.risk], background: `${LK_RISK[r.risk]}14`, border: `1px solid ${LK_RISK[r.risk]}33` }}>{r.risk}</span></td>
                <td className="text-xs text-[#8A8175]">{r.fuzzer}</td>
                <td className="text-center text-xs">{r.dns_a?.length ? "A" : "—"} / {r.dns_mx?.length ? <span className="text-[#A5300F] font-semibold">MX</span> : "—"}</td>
                <td>
                  <select value={r.status} onChange={(e) => setStatus(r, e.target.value)} disabled={!canScan}
                    data-testid={`lookalike-status-${r.domain}`}
                    className="text-xs bg-white border border-[#E7D9C6] rounded px-2 py-1 focus:outline-none disabled:opacity-60">
                    {LK_STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={5} className="py-10 text-center text-[#B5AC9E]">No lookalikes found yet. {canScan ? "Run a lookalike scan to find typo-squatted domains." : "Awaiting an analyst scan."}</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

function useScan(orgId, endpoint) {
  const [scanning, setScanning] = useState(false);
  const run = async (domainId, onDone) => {
    setScanning(true);
    try {
      const { data } = await http.post(`/attack/${endpoint}/scan`, { org_id: orgId, domain_id: domainId });
      const started = Date.now();
      const poll = async () => {
        const r = await http.get(`/attack/${endpoint}/scans/${data.id}`, { params: { org_id: orgId } });
        if (r.data.status === "completed") { onDone(r.data.summary); toast.success("Scan complete"); setScanning(false); }
        else if (r.data.status === "failed") { toast.error(r.data.error_message || "Scan failed"); setScanning(false); }
        else if (Date.now() - started > 180000) { toast.message("Still running — check back shortly."); setScanning(false); }
        else setTimeout(poll, 3000);
      };
      setTimeout(poll, 2500);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); setScanning(false); }
  };
  return [scanning, run];
}

function certExpiry(notAfter) {
  if (!notAfter) return <span className="text-[#8A8175]">—</span>;
  const d = new Date(notAfter);
  const days = Math.ceil((d - new Date()) / 86400000);
  const date = d.toLocaleDateString("en-GB");
  if (days < 0) return <span className="font-semibold text-[#A5300F]">{date} · Expired</span>;
  if (days <= 30) return <span className="font-semibold text-[#E39629]">{date} · {days}d left</span>;
  return <span className="text-[#5C554C]">{date} · {days}d</span>;
}

function CtTab({ domainId, orgId, canScan }) {
  const [certs, setCerts] = useState([]);
  const [scanning, run] = useScan(orgId, "ct");
  const load = useCallback(() => http.get("/attack/ct", { params: { org_id: orgId, domain_id: domainId } }).then((r) => setCerts(r.data)), [orgId, domainId]);
  useEffect(() => { load(); }, [load]);
  const isNew = (c) => c.new;
  return (
    <div className="space-y-5" data-testid="ct-tab">
      <div className="flex items-center justify-between">
        <p className="text-sm text-[#8A8175]">{certs.length} certificate(s) observed in Certificate Transparency logs.</p>
        {canScan && <button onClick={() => run(domainId, load)} disabled={scanning} data-testid="run-ct-btn"
          className="flex items-center gap-2 bg-[#A5300F] text-white font-semibold text-sm px-4 py-2 rounded-md hover:bg-[#8F2A0D] disabled:opacity-60">
          {scanning ? <Spinner /> : <ScrollText className="w-4 h-4" />} {scanning ? "Scanning…" : "Refresh CT logs"}</button>}
      </div>
      <Card>
        <table className="w-full text-sm" data-testid="ct-table">
          <thead><tr className="text-[11px] uppercase tracking-wider text-[#8A8175] text-left bg-[#FFF5EB] border-b border-[#ECE6DD]">
            <th className="py-2.5 px-4">Common name</th><th>SAN (dns_names)</th><th>Issuer</th><th>Valid from</th><th>Valid until</th><th>Source</th></tr></thead>
          <tbody>
            {certs.map((c) => (
              <tr key={c.cert_id} className="border-b border-[#F4EFE8] hover:bg-[#FBF7F1]" data-testid={`ct-row-${c.cert_id}`}>
                <td className="py-2.5 px-4 font-semibold font-mono">{c.common_name || "—"}{isNew(c) && <span className="ml-2 text-[10px] font-bold text-white bg-[#A5300F] rounded px-1.5 py-0.5">NEW</span>}</td>
                <td className="text-xs text-[#5C554C]">{(c.dns_names || []).slice(0, 4).join(", ")}{(c.dns_names || []).length > 4 ? ` +${c.dns_names.length - 4}` : ""}</td>
                <td className="text-xs text-[#8A8175]">{(c.issuer || "").slice(0, 40)}</td>
                <td className="text-xs text-[#8A8175]">{c.not_before ? new Date(c.not_before).toLocaleDateString("en-GB") : "—"}</td>
                <td className="text-xs" data-testid={`ct-expiry-${c.cert_id}`}>{certExpiry(c.not_after)}</td>
                <td className="text-xs text-[#8A8175]">{c.source}</td>
              </tr>
            ))}
            {certs.length === 0 && <tr><td colSpan={6} className="py-10 text-center text-[#B5AC9E]">No certificates yet. {canScan ? "Refresh CT logs to pull issued certificates." : "Awaiting an analyst scan."}</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

const EXP_SEV = { critical: "#A5300F", high: "#D55816", medium: "#E39629", low: "#7F5F52", info: "#9AA0A6" };

function ExposureTab({ domainId, orgId, canScan }) {
  const [rows, setRows] = useState([]);
  const [scanning, run] = useScan(orgId, "exposure");
  const load = useCallback(() => http.get("/attack/exposure", { params: { org_id: orgId, domain_id: domainId } }).then((r) => setRows(r.data)), [orgId, domainId]);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-5" data-testid="exposure-tab">
      <div className="flex items-center justify-between">
        <p className="text-sm text-[#8A8175]">{rows.length} exposed-surface finding(s) across live assets.</p>
        {canScan && <button onClick={() => run(domainId, load)} disabled={scanning} data-testid="run-exposure-btn"
          className="flex items-center gap-2 bg-[#A5300F] text-white font-semibold text-sm px-4 py-2 rounded-md hover:bg-[#8F2A0D] disabled:opacity-60">
          {scanning ? <Spinner /> : <Bug className="w-4 h-4" />} {scanning ? "Scanning…" : "Run exposure scan"}</button>}
      </div>
      {scanning && <Card className="p-4 text-sm text-[#8A8175]">Probing live assets for exposed paths (.git/.env), source maps, JS secrets, permissive CORS and GraphQL introspection…</Card>}
      <Card>
        <div className="divide-y divide-[#F4EFE8]" data-testid="exposure-findings">
          {rows.map((f) => (
            <div key={f.id} className="p-4 flex gap-4" data-testid={`exposure-${f.id}`}>
              <span className="shrink-0 px-2 py-0.5 text-[11px] font-bold rounded uppercase self-start" style={{ color: EXP_SEV[f.severity], background: `${EXP_SEV[f.severity]}14`, border: `1px solid ${EXP_SEV[f.severity]}33` }}>{f.severity}</span>
              <div className="min-w-0">
                <div className="font-semibold text-sm text-[#323232]">{f.title}<span className="ml-2 text-[10px] uppercase tracking-wider text-[#B5AC9E] font-bold">{f.category}</span></div>
                <div className="text-sm text-[#5C554C] mt-0.5">{f.detail}</div>
                <div className="text-xs text-[#8A8175] mt-1 font-mono break-all">{f.url}</div>
              </div>
            </div>
          ))}
          {rows.length === 0 && <div className="p-10 text-center text-[#2E7D32] font-semibold">No exposed-surface issues detected.</div>}
        </div>
      </Card>
    </div>
  );
}

